import React, { useState, useRef, useEffect } from "react";
import { Button, Typography, CircularProgress } from "@mui/material";
import { generateAudioTimestamp, triggerUploadCompletion } from './Audiorecordertab';
import axios from "axios";
import WaveSurfer from "wavesurfer.js";
import "./config/axiosConfig";
import { useSiteSettings } from './context/SiteSettingsContext';
import Portal from './components/Portal';
import ModalWrapper from './components/Modal/ModalWrapper';
import { Link } from "react-router-dom"; // Stelle sicher, dass NavLink importiert ist

import HeadphonesIcon from '@mui/icons-material/Headphones';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MicIcon from '@mui/icons-material/Mic';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LabelIcon from '@mui/icons-material/Label';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ArticleIcon from '@mui/icons-material/Article';

type UploadTabProps = {
  onUploadError?: (message: string) => void;
};

const UploadTab: React.FC<UploadTabProps> = ({ onUploadError }) => {
  const [isUpLoading, setIsUpLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false); // Zustand für die Dateivorschau
  const [originalName, setOriginalName] = useState(''); // Zustand für den Originalnamen
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const sessionIdRef = useRef<string>(''); // Ref für session_id
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const [showTipsDialog, setShowTipsDialog] = useState(false);
  
  useEffect(() => {
    if (audioUrl && waveformRef.current) {
      if (waveSurferRef.current) {
        waveSurferRef.current.destroy();
      }

      waveSurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#ddd',
        progressColor: '#32768D',
        cursorColor: '#333',
        height: 120,
        barHeight: 2,
        barWidth: 1,
      });

      waveSurferRef.current.load(audioUrl);
    }
  }, [audioUrl]);

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setOriginalName(file.name.replace(/\.[^/.]+$/, ""));
    setAudioUrl(URL.createObjectURL(file));
    setShowPreview(true);
    setUploadProgress(0); // Setze den Fortschritt rechtzeitig zurück
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.'));
    const timestampFilename = generateAudioTimestamp(extension);
    const chunkSize = 1024 * 1024 * 0.5; // 0.5 MB
    const totalChunks = Math.ceil(selectedFile.size / chunkSize);
    let uploadedChunks = 0;
    sessionIdRef.current = timestampFilename .replace(/\.[^/.]+$/, ".mp3");;
    setShowPreview(false);
    setIsUpLoading(true);
    

    for (let i = 0; i < totalChunks; i++) {
      const chunk = selectedFile.slice(i * chunkSize, (i + 1) * chunkSize);
      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('filename', timestampFilename);
      formData.append('session_id', sessionIdRef.current);
      formData.append('originalName', originalName);
      formData.append('chunkIndex', String(i));
      formData.append('totalChunks', String(totalChunks));

      try {
        await axios.post('/api/services/upload-audio/', formData, {
          onUploadProgress: () => {
            const percentCompleted = Math.round(((uploadedChunks + 1) / totalChunks) * 100);
            setUploadProgress(percentCompleted);
          }
        });

        uploadedChunks++;
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Hochladen:', error);
        const message = "Das Hochladen ist fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.";
        if (onUploadError) {
          onUploadError(message);
        } else {
          alert(message);
        }
        setIsUpLoading(false);
        return;
      }
    }

    setIsUpLoading(false);
    setUploadProgress(100);
    setIsLoading(false);

   // try {
   //   await triggerUploadCompletion(sessionIdRef.current, timestampFilename, originalName, 0);
   // } catch (error) {
    //  if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim automatischen Abschluss der Verarbeitung:', error);
    //  alert('Fehler beim automatischen Abschluss der Verarbeitung. Bitte versuchen Sie es manuell.');
   // }

    if (audioUrl) URL.revokeObjectURL(audioUrl);
    
    setShowPreview(false);
    setSelectedFile(null);
    setAudioUrl(null);
    setOriginalName('');
    setUploadProgress(0);
  };


  return (
    <div className="tw-text-gray-500 tw-flex-1" style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>
      
    {!showPreview && (
      <div data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-113-trigger-file" id="radix-113-content-file" tabIndex={0}>
        <div role="presentation" tabIndex={0} className="tw-flex tw-h-full tw-flex-col  tw-text-center tw-relative">
          <div className="tw-mb-2">Audioaufnahme einer ganzen Sitzung oder Zusammenfassung </div>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Unterstützte Formate: MP3, WAV, M4A, AAC, FLAC
          </Typography>
          <div className="tw-flex-1 tw-flex tw-items-center">
            <div className="tw-w-full">
              <div className="tw-flex tw-items-center tw-justify-center">
                <input
                  type="file"
                  accept="audio/mpeg,.mp3,audio/mpeg3,.mp3,audio/x-mpeg-3,.mp3,audio/mp3,.mp3,audio/x-mp3,.mp3,audio/x-mpeg,.mp3,audio/mpg,.mp3,audio/x-mpg,.mp3,audio/mp4,.mp4,.m4a,audio/x-mp4,.mp4,audio/x-m4a,.m4a,audio/m4a,.m4a,audio/ogg,.ogg,audio/x-ogg,.ogg,audio/webm,.webm,.weba,audio/vnd.wav,.wav,audio/x-wav,.wav,audio/wav,.wav,audio/aiff,.aiff,audio/x-aiff,.aiff"
                  ref={inputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal"
                >
                  <div className="_btnFlex_19rto_32">
                    <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                        <path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M12.5 15.5V9.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M9.5 11.5L12.5 8.5L15.5 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </div>
                    Audiodatei auswählen
                  </div>
                </button>
              </div>
              <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mt-2">
              <img className="tw-w-4" src="/dsgvo.png" alt="!" />
                <span>100% DSGVO-konform</span>
                <div>
                  <button className="tw-flex" data-state="closed">
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.00033 1.32487C11.9537 1.32487 15.167 4.5382 15.167 8.49154C15.167 12.4449 11.9537 15.6582 8.00033 15.6582C4.04699 15.6582 0.833658 12.4449 0.833658 8.49154C0.833658 4.5382 4.04699 1.32487 8.00033 1.32487ZM8.00033 14.6582C11.4003 14.6582 14.167 11.8915 14.167 8.49154C14.167 5.09154 11.4003 2.32487 8.00033 2.32487C4.60033 2.32487 1.83366 5.09154 1.83366 8.49154C1.83366 11.8915 4.60033 14.6582 8.00033 14.6582Z" fill="#8F8F8F"></path>
                      <path d="M8 7.32487C8.27333 7.32487 8.5 7.55154 8.5 7.82487V11.1582C8.5 11.4315 8.27333 11.6582 8 11.6582C7.72667 11.6582 7.5 11.4315 7.5 11.1582V7.82487C7.5 7.55154 7.72667 7.32487 8 7.32487Z" fill="#8F8F8F"></path>
                      <path d="M8.00033 5.15828C8.08699 5.15828 8.17366 5.17828 8.25366 5.21161C8.33366 5.24495 8.40699 5.29162 8.47366 5.35162C8.53366 5.41828 8.58033 5.48495 8.61366 5.57162C8.64699 5.65162 8.66699 5.73828 8.66699 5.82495C8.66699 5.91161 8.64699 5.99828 8.61366 6.07828C8.58033 6.15828 8.53366 6.23161 8.47366 6.29828C8.40699 6.35828 8.33366 6.40495 8.25366 6.43828C8.09366 6.50495 7.90699 6.50495 7.74699 6.43828C7.66699 6.40495 7.59366 6.35828 7.52699 6.29828C7.46699 6.23161 7.42033 6.15828 7.38699 6.07828C7.35366 5.99828 7.33366 5.91161 7.33366 5.82495C7.33366 5.73828 7.35366 5.65162 7.38699 5.57162C7.42033 5.48495 7.46699 5.41828 7.52699 5.35162C7.59366 5.29162 7.66699 5.24495 7.74699 5.21161C7.82699 5.17828 7.91366 5.15828 8.00033 5.15828Z" fill="#8F8F8F"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="tw-text-sm">
                <div>Diktatdauer: 1-10min</div>
                <div>Aufnahmedauer: 2-60 min</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    {showPreview && (
      <div data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-641-trigger-file" id="radix-641-content-file" tabIndex={0}>
        <div className="tw-flex tw-h-full tw-flex-col  tw-text-center tw-relative">
          <div className="tw-mb-2">Audioaufnahme einer ganzen Sitzung oder Zusammenfassung </div>
          <div className="tw-flex-1 tw-flex tw-items-center">
            <div className="tw-w-full">
              <div className="_previews_1aueu_187">
                <div className="_preview_16d6j_1">
                  <button className="_close_16d6j_15" onClick={() => {
                    if (audioUrl) URL.revokeObjectURL(audioUrl);
                    setShowPreview(false);
                    setSelectedFile(null);
                    setAudioUrl(null);
                    setOriginalName('');
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.00004 14.6666C11.6667 14.6666 14.6667 11.6666 14.6667 7.99992C14.6667 4.33325 11.6667 1.33325 8.00004 1.33325C4.33337 1.33325 1.33337 4.33325 1.33337 7.99992C1.33337 11.6666 4.33337 14.6666 8.00004 14.6666Z" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                      <path d="M6.11328 9.88661L9.88661 6.11328" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                      <path d="M9.88661 9.88661L6.11328 6.11328" stroke="#32768D" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                  </button>
                  <div className="_nameWrapperFlex_16d6j_37">
                    <div>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" fill="#eee"/></svg>
                    </div>
                    <div className="_nameWrapper_16d6j_37">
                      <div className="_fileName_16d6j_51">
                        <div className="_firstChunk_16d6j_61">{originalName}</div>
                        <div className="_lastChunk_16d6j_66">.wav</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="_previewPlayer_16d6j_70 _ready_16d6j_77">
                      <div>
                        <button
                          className="_playerControl_16d6j_103"
                          onClick={() => waveSurferRef.current && waveSurferRef.current.playPause()}
                        >
                          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-6 tw-h-6">
                            <path d="M5.74699 14.8491C5.22033 14.8491 4.72033 14.7224 4.28033 14.4691C3.24033 13.8691 2.66699 12.6491 2.66699 11.0424V6.28909C2.66699 4.67576 3.24033 3.46243 4.28033 2.86243C5.32033 2.26243 6.66033 2.37576 8.06033 3.18243L12.1737 5.55576C13.567 6.36243 14.3403 7.46909 14.3403 8.6691C14.3403 9.86909 13.5737 10.9758 12.1737 11.7824L8.06033 14.1558C7.25366 14.6158 6.46699 14.8491 5.74699 14.8491ZM5.74699 3.47576C5.38699 3.47576 5.06699 3.55576 4.78033 3.72243C4.06033 4.13576 3.66699 5.04909 3.66699 6.28909V11.0358C3.66699 12.2758 4.06033 13.1824 4.78033 13.6024C5.50033 14.0224 6.48699 13.9024 7.56033 13.2824L11.6737 10.9091C12.747 10.2891 13.3403 9.49576 13.3403 8.66243C13.3403 7.82909 12.747 7.03576 11.6737 6.41576L7.56033 4.04243C6.90699 3.66909 6.29366 3.47576 5.74699 3.47576Z" fill="#32768D"/>
                          </svg>
                        </button>
                      </div>
                      <div ref={waveformRef} style={{ width: '100%' }} />
                    </div>
                  </div>
                </div>
              </div>
              <button onClick={handleUpload} className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal sm:!tw-w-auto !tw-w-full">
                <div className="_btnFlex_19rto_32">
                <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                        <path d="M12.5 22C18.0228 22 22.5 17.5228 22.5 12C22.5 6.47715 18.0228 2 12.5 2C6.97715 2 2.5 6.47715 2.5 12C2.5 17.5228 6.97715 22 12.5 22Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M12.5 15.5V9.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                        <path d="M9.5 11.5L12.5 8.5L15.5 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path>
                      </svg>
                    </div>
                  Datei hochladen
                </div>
              </button>
            </div>
          </div>
          <div className="tw-text-sm">
          <div>Diktatdauer: 1-10min</div>
          <div>Aufnahmedauer: 2-60 min</div>
          </div>
        </div>
      </div>
    )}
    {isUpLoading && (
      <div className="progress">
        <div className="progress__bar progress__bar--success" style={{ width: `${uploadProgress}%` }}>
          <div className="progress__bar__stripes"></div>
        </div>
        <div className="progress__status">
          <div className="tw-w-full progress__status mobile__pending">
            <div className="status note">
              <p>Upload... </p>
              {/*<p className="!tw-text-[#8F8F8F]">about 4 minutes left</p>*/}
            </div>
          </div>
        </div>
      </div>
    )}
    <div className="tab-tip tw-mt-1"><button className="btn-reset tw-text-primary-500 tw-underline" onClick={() => setShowTipsDialog(true)}><span>Tipps zum Datei-Upload</span></button>
    </div>{showTipsDialog && (
  <Portal>
    <ModalWrapper onClose={() => setShowTipsDialog(false)} title="Tipps zum Upload">
      <div className="tw-grow tw-flex tw-flex-col">
        <ul className="sc-gEvEer fteAEG tw-grid tw-gap-4">
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <AssignmentTurnedInIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div>
                Stellen Sie vor dem Upload sicher, dass Ihnen das Einverständnis Ihrer Klient:innen zur Nutzung von LumaNote und zur Verarbeitung der Aufnahme vorliegt. <Link to="/settings/clientconsent" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Einverständnisformular herunterladen</Link>
              </div>
            </div>
          </li>
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <MicIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div><span className="tw-font-bold">Für ein optimales Ergebnis vermeiden Sie möglichst Störgeräusche, Pausen und Nebengespräche und achten Sie auf gleichmäßige Lautstärke sowie gut verständliche Stimmen.</span></div>
            </div>
          </li>
          
          
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <ChatBubbleOutlineIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div>
                            Eine präzise Benennung von Interventionen und Fachbegriffen hilft LumaNote, die Qualität der Dokumentation zu verbessern.
              </div>
            </div>
          </li>
          
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <PlayCircleOutlineIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div>
                Sie können nahezu alle gängigen Audioformate hochladen (z.&nbsp;B. MP3, WAV, M4A, AAC, FLAC).
              </div>
            </div>
          </li>
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <DeleteForeverIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div>
                Hochgeladene Dateien werden am Ende desselben Tages automatisch gelöscht. <a target="_blank" rel="noreferrer" href="https://lumanote.de/security" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Mehr erfahren</a>
              </div>
            </div>
          </li>
          
          <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                <ContentCutIcon fontSize="small" className="tw-max-w-full" />
              </div>
              <div>
                Laden Sie bei für jede Sitzungen getrennte Dateien hoch.
              </div>
            </div>
          </li>
          
        </ul>
      </div>
    </ModalWrapper>
  </Portal>
)}</div>
  );
};

export default UploadTab;
